import { useState } from "react";

/**
 * Past and current conversations, beside the chat.
 *
 * Until now a conversation was unreachable the moment it left the screen: the
 * history endpoint returned a summary and nothing else, and the only way back
 * into Lucas was to send a message. The list is the missing door.
 *
 * "Nova conversa" ends the current one rather than creating one. There is no
 * create call: `getOrCreateConversation` always returns the user's *active*
 * conversation, so a new one can only begin once the current is closed —
 * closing is also what generates the summary that makes it readable here.
 *
 * A past conversation opens read-only. Sending into it would silently land in
 * the active conversation instead, which is the kind of quiet lie this screen
 * has been chasing out.
 */

function PlusGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className="h-3.5 w-3.5 flex-none">
      <path
        d="M8 3.5v9M3.5 8h9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TrashGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className="h-3.5 w-3.5">
      <path
        d="M2.75 4.25h10.5M6.5 4.25V3a.75.75 0 01.75-.75h1.5A.75.75 0 019.5 3v1.25M12 4.25l-.5 8a1.25 1.25 0 01-1.25 1.15h-4.5A1.25 1.25 0 014.5 12.25l-.5-8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const truncate = (text, max = 46) =>
  text.length > max ? `${text.slice(0, max)}…` : text;

/**
 * Uma conversa nunca teve título.
 *
 * Ordem: o resumo gerado no fechamento; senão a primeira pergunta, que é o
 * título de fato; senão a data. O resumo só existe quando `generateSummary`
 * conseguiu rodar, então cair direto para a data — como acontecia — produzia
 * uma coluna de "01 de ago." repetido que não identifica nada.
 */
function conversationLabel(conversation) {
  const summary = conversation.summary?.trim();
  if (summary) return truncate(summary);

  const first = conversation.firstMessage?.trim();
  if (first) return truncate(first);

  return new Date(
    conversation.updatedAt || conversation.createdAt,
  ).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

/**
 * Altura fixa com centragem em vez de padding + line-height: com padding o texto
 * assenta na base do realce quando a fonte tem métricas assimétricas, que foi o
 * que apareceu na tela. Altura definida não depende da fonte.
 */
const ROW =
  "relative flex h-9 w-full items-center rounded-lg pl-3 pr-9 text-left transition-colors";
const SELECTED =
  "bg-black/[.06] font-medium text-[#15161f] dark:bg-white/[.09] dark:text-white";
const IDLE =
  "text-[#5c5f70] hover:bg-black/[.035] hover:text-[#15161f] dark:text-white/55 dark:hover:bg-white/[.06] dark:hover:text-white";
const SECTION =
  "px-3 pb-1 pt-3 font-mono text-[10px] uppercase tracking-[.12em] text-[#9a9dad] dark:text-white/35";

export default function ConversationList({
  conversations = [],
  activeLabel,
  viewingId,
  onOpenConversation,
  onResumeActive,
  onNewChat,
  onDeleteConversation,
  hasActiveConversation,
}) {
  // Exclusão é irreversível e o alvo é uma linha estreita numa lista longa —
  // um clique acidental não pode destruir a conversa.
  const [confirmingId, setConfirmingId] = useState(null);

  return (
    <div className="flex h-full flex-col gap-3 overflow-hidden">
      <button
        type="button"
        onClick={onNewChat}
        className="flex h-9 flex-none items-center gap-2 rounded-xl border border-black/10 bg-white/70 px-3 text-[13px] font-semibold text-[#15161f] transition-colors hover:bg-white dark:border-white/[.14] dark:bg-white/[.06] dark:text-white dark:hover:bg-white/[.11]"
      >
        <PlusGlyph />
        Nova conversa
      </button>

      <div className="custom-scrollbar flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto pr-1">
        {hasActiveConversation && (
          <>
            <div className={SECTION}>Em andamento</div>
            <button
              type="button"
              onClick={onResumeActive}
              className={`${ROW} ${viewingId ? IDLE : SELECTED}`}
            >
              <span className="truncate text-[13px]">
                {activeLabel || "Conversa atual"}
              </span>
            </button>
          </>
        )}

        {conversations.length > 0 && <div className={SECTION}>Encerradas</div>}

        {conversations.map((conversation) => {
          const label = conversationLabel(conversation);
          const isConfirming = confirmingId === conversation.id;

          return (
            // `group` no wrapper, não na linha: o botão de excluir é IRMÃO da
            // linha, então `group-hover` precisa do ancestral comum.
            <div key={conversation.id} className="group relative">
              <button
                type="button"
                onClick={() => onOpenConversation(conversation.id)}
                className={`${ROW} ${viewingId === conversation.id ? SELECTED : IDLE}`}
              >
                <span className="truncate text-[13px]">{label}</span>
              </button>

              {isConfirming ? (
                <span className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-1">
                  <button
                    type="button"
                    aria-label={`Confirmar exclusão de ${label}`}
                    onClick={() => {
                      onDeleteConversation(conversation.id);
                      setConfirmingId(null);
                    }}
                    className="rounded-md bg-[#ff5a4d] px-2 py-1 text-[10.5px] font-bold text-white transition-opacity hover:opacity-90"
                  >
                    Excluir
                  </button>
                  <button
                    type="button"
                    aria-label={`Cancelar exclusão de ${label}`}
                    onClick={() => setConfirmingId(null)}
                    className="rounded-md px-1.5 py-1 text-[10.5px] font-semibold text-[#5c5f70] transition-colors hover:text-[#15161f] dark:text-white/50 dark:hover:text-white"
                  >
                    Não
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  aria-label={`Excluir ${label}`}
                  onClick={(event) => {
                    // A linha inteira é clicável; sem isto, tentar excluir
                    // abriria a conversa por baixo.
                    event.stopPropagation();
                    setConfirmingId(conversation.id);
                  }}
                  className="absolute right-1 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-md text-[#9a9dad] opacity-0 transition-all hover:bg-black/[.06] hover:text-[#c0392b] focus-visible:opacity-100 group-hover:opacity-100 dark:text-white/40 dark:hover:bg-white/10 dark:hover:text-[#ff8c80]"
                >
                  <TrashGlyph />
                </button>
              )}
            </div>
          );
        })}

        {!hasActiveConversation && conversations.length === 0 && (
          <p className="m-0 px-3 py-2 text-[12.5px] leading-[1.5] text-[#9a9dad] dark:text-white/35">
            Suas conversas com o Lucas aparecem aqui.
          </p>
        )}
      </div>
    </div>
  );
}
